import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CalendarPlus, Copy, Share2, User } from 'lucide-react';
import { aiInterviewsAPI, errMsg } from '../../../lib/aiInterviewApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { copyText, fmtDate, LANGS, Pill, PROCTORING, StatusPill } from '../recruiter/common';
import { schedulingAPI } from '../../../lib/schedulingApi';
import ShareReportDialog from './ShareReportDialog';
import ScheduleDialog from '../../scheduling/ScheduleDialog';
import '../../scheduling/scheduling.css';

const ACTIONS = [
  ['resend', 'Resend invite', ['invited', 'opened', 'consented']],
  ['extend', 'Extend link 7 days', ['invited', 'opened', 'consented', 'expired']],
  ['reopen', 'Reopen interview', ['interrupted']],
  ['rescore', 'Rescore', ['score_error', 'completed']],
  ['revoke', 'Revoke invite', ['invited', 'opened', 'consented', 'interrupted']],
];
const SCOPES = [['recordings', 'Recordings & snapshots'], ['biometric', 'ID photo & selfie'], ['all', 'Everything (transcript, report, media)']];

const DeleteDialog = ({ open, onOpenChange, d, onDone }) => {
  const [scope, setScope] = useState('recordings');
  const [reason, setReason] = useState('');
  const go = async () => {
    try {
      const { data } = await aiInterviewsAPI.deleteData(d.id, { scope, reason });
      toast.success(`Deleted ${Object.values(data.deleted || {}).reduce((a, b) => a + b, 0)} items`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not delete'));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="iv" data-testid="report-delete-dialog">
        <DialogHeader><DialogTitle>Delete interview data</DialogTitle><DialogDescription>Deletion is permanent and recorded in the deletion log.</DialogDescription></DialogHeader>
        <div className="iv-options" style={{ gridTemplateColumns: '1fr' }}>
          {SCOPES.map(([k, l]) => <button type="button" key={k} className={`iv-option ${scope === k ? 'is-on' : ''}`} onClick={() => setScope(k)} data-testid={`delete-scope-${k}`}><b>{l}</b></button>)}
        </div>
        <input className="iv-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. candidate request)" data-testid="delete-reason-input" />
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => onOpenChange(false)}>Cancel</button>
          <button type="button" className="iv-btn iv-btn-danger" onClick={go} data-testid="delete-confirm-btn">Delete</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ReportHeader({ d, reload }) {
  const navigate = useNavigate();
  const [del, setDel] = useState(false);
  const [share, setShare] = useState(false);
  const [sched, setSched] = useState(false);
  const [pools, setPools] = useState(null);
  const openSchedule = () => {
    schedulingAPI.pools().then(({ data }) => {
      if (!data.length) { toast.error('Create an interviewer pool first (Interview Scheduling → Interviewer pools)'); return; }
      setPools(data);
      setSched(true);
    }).catch(() => toast.error('Could not load interviewer pools'));
  };
  const act = async (a) => {
    try {
      await aiInterviewsAPI.action(d.id, a);
      toast.success('Done');
      reload();
    } catch (e) {
      toast.error(errMsg(e, 'Action failed'));
    }
  };
  return (
    <>
      <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => navigate('/interviews')} data-testid="report-back-btn"><ArrowLeft size={14} /> AI Interviews</button>
      <div className="iv-rhead" style={{ marginTop: '0.9rem' }}>
        <div>
          <div className="iv-eyebrow">{d.job_title}</div>
          <h1 className="iv-rtitle" data-testid="report-candidate-name">{d.candidate_name}</h1>
          <div className="iv-row iv-small" style={{ marginTop: '0.35rem' }}>
            <StatusPill status={d.status} testId="report-status" />
            {d.identity_status && d.identity_status !== 'not_started' && <Pill tone={d.identity_status === 'pass' ? 'green' : 'amber'}>ID {d.identity_status === 'pass' ? 'verified' : 'needs review'}</Pill>}
            <span>{d.candidate_email}</span>
            <span>· {LANGS[d.language] || d.language} · {PROCTORING[d.proctoring_level]} proctoring · invited {fmtDate(d.created_at)}{d.completed_at ? ` · completed ${fmtDate(d.completed_at)}` : ''}</span>
          </div>
        </div>
        <div className="iv-row">
          {d.report && <button type="button" className="iv-btn iv-btn-primary iv-btn-sm" onClick={() => setShare(true)} data-testid="report-share-btn"><Share2 size={14} /> Share report</button>}
          {d.candidate_id && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={openSchedule} data-testid="report-schedule-btn"><CalendarPlus size={14} /> Schedule human interview</button>}
          {d.candidate_id && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => navigate(`/candidates/${d.candidate_id}`)} data-testid="report-candidate-profile-btn"><User size={14} /> Profile</button>}
          {d.link && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => copyText(d.link, toast)} data-testid="report-copy-link-btn"><Copy size={14} /> Candidate link</button>}
          {ACTIONS.filter(([, , st]) => st.includes(d.status)).map(([a, l]) => (
            <button type="button" key={a} className={`iv-btn iv-btn-sm ${a === 'revoke' ? 'iv-btn-danger' : 'iv-btn-ghost'}`} onClick={() => act(a)} data-testid={`report-action-${a}`}>{l}</button>
          ))}
          {d.status !== 'data_deleted' && <button type="button" className="iv-btn iv-btn-danger iv-btn-sm" onClick={() => setDel(true)} data-testid="report-delete-data-btn">Delete data</button>}
        </div>
      </div>
      <DeleteDialog open={del} onOpenChange={setDel} d={d} onDone={reload} />
      {d.report && <ShareReportDialog open={share} onOpenChange={setShare} d={d} />}
      <ScheduleDialog open={sched} onOpenChange={setSched} pools={pools} preset={{ jobId: d.job_id, candidateId: d.candidate_id, candidateName: d.candidate_name, aiInterviewId: d.id }} />
    </>
  );
}
